import React, { useEffect, useState } from 'react'
import api from '../services/api.ts'

interface Share {
  id: string
  share_token: string
  shareUrl: string
  expires_at: string | null
  is_active: boolean
  created_at: string
  recipient_email: string | null
  recipient_name: string | null
  expired: boolean
}

interface ShareReportDialogProps {
  reportId: string
  reportName: string
  onClose: () => void
}

export default function ShareReportDialog({
  reportId,
  reportName,
  onClose,
}: ShareReportDialogProps) {
  const [shares, setShares] = useState<Share[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [sending, setSending] = useState<string | null>(null)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const fetchShares = async () => {
    try {
      const res = await api.get(`/api/reports/${reportId}/shares`)
      setShares(res.data.shares || [])
    } catch {
      setError('Failed to load shares')
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchShares()
  }, [reportId])

  const handleCreate = async (sendEmail: boolean) => {
    setError(null)
    setCreating(true)
    try {
      const res = await api.post(`/api/reports/${reportId}/shares`, {
        recipientEmail: recipientEmail || undefined,
        recipientName: recipientName || undefined,
      })
      const newShare = res.data.share

      if (sendEmail && newShare.id && recipientEmail) {
        await api.post(`/api/reports/${reportId}/shares/${newShare.id}/send`)
      }

      if (!sendEmail && newShare.shareUrl) {
        try {
          await navigator.clipboard.writeText(newShare.shareUrl)
          setCopied(newShare.id)
          setTimeout(() => setCopied(null), 2000)
        } catch {
          // Clipboard API may fail on non-HTTPS or unfocused pages — show URL as fallback
          window.prompt('Copy this share link:', newShare.shareUrl)
        }
      }

      setRecipientEmail('')
      setRecipientName('')
      await fetchShares()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create share')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (shareId: string) => {
    try {
      await api.delete(`/api/reports/${reportId}/shares/${shareId}`)
      await fetchShares()
    } catch {
      setError('Failed to revoke share')
    }
  }

  const handleResend = async (shareId: string) => {
    setSending(shareId)
    try {
      await api.post(`/api/reports/${reportId}/shares/${shareId}/send`)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to send email')
    } finally {
      setSending(null)
    }
  }

  const copyLink = async (url: string, shareId: string) => {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      window.prompt('Copy this share link:', url)
      return
    }
    setCopied(shareId)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Share Report</h2>
            <p className="text-sm text-gray-500">{reportName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto max-h-[60vh] space-y-6">
          {/* Create new share */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Create New Share Link</h3>
            <input
              type="text"
              placeholder="Recipient name (optional)"
              value={recipientName}
              onChange={e => setRecipientName(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="email"
              placeholder="Recipient email (optional)"
              value={recipientEmail}
              onChange={e => setRecipientEmail(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex gap-2">
              <button
                onClick={() => handleCreate(true)}
                disabled={creating || !recipientEmail}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg text-white ${
                  creating || !recipientEmail
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {creating ? 'Creating...' : 'Create & Send Email'}
              </button>
              <button
                onClick={() => handleCreate(false)}
                disabled={creating}
                className={`px-4 py-2 text-sm font-medium rounded-lg border ${
                  creating
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Copy Link Only
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Existing shares */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Existing Share Links {shares.length > 0 && `(${shares.length})`}
            </h3>
            {loading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : shares.length === 0 ? (
              <p className="text-sm text-gray-400">No shares yet</p>
            ) : (
              <div className="space-y-2">
                {shares.map(share => (
                  <div
                    key={share.id}
                    className={`p-3 border rounded-lg ${
                      !share.is_active || share.expired
                        ? 'border-gray-200 bg-gray-50 opacity-60'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 truncate">
                          {share.recipient_email || 'No recipient'}
                          {share.recipient_name && ` (${share.recipient_name})`}
                        </p>
                        <p className="text-xs text-gray-500">
                          Created {new Date(share.created_at).toLocaleDateString()}
                          {' · '}
                          {!share.is_active
                            ? 'Revoked'
                            : share.expired
                              ? 'Expired'
                              : share.expires_at
                                ? `Expires ${new Date(share.expires_at).toLocaleDateString()}`
                                : 'Never expires'}
                        </p>
                      </div>
                      {share.is_active && !share.expired && (
                        <div className="flex gap-1 ml-2 flex-shrink-0">
                          <button
                            onClick={() => copyLink(share.shareUrl, share.id)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                            title="Copy link"
                          >
                            {copied === share.id ? (
                              <svg
                                className="w-4 h-4 text-green-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                                />
                              </svg>
                            )}
                          </button>
                          {share.recipient_email && (
                            <button
                              onClick={() => handleResend(share.id)}
                              disabled={sending === share.id}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                              title="Resend email"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => handleRevoke(share.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                            title="Revoke"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
